import React, { useRef, useState } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Editor } from '@tinymce/tinymce-react';
import {
  Cancel,
  DownloadForOffline,
  Edit,
  MoreVert,
  OpenInNew,
} from '@mui/icons-material';
import { List, ListItem, Popover } from '@mui/material';
import Spinner from './spinner/Spinner';
import NameUrlDialog from './NameUrlDialog';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import { fileToBase64 } from '../helpers/convertToBase64';
import tinyMCEConfigs from '../utils/tinymce';
import usePermissions from '../hooks/usePermissions';
import useGetUserRole from '../hooks/useGetUserRole';

const schema = yup
  .object({
    courseName: yup.string().required('Course name is required'),
    courseDescription: yup.string().required('Course description is required'),
    courseImage: yup
      .mixed()
      .test('fileSize', 'Please upload image less than 2MB', (value) => {
        if (!(value?.[0] instanceof File)) return true;
        return value[0].size <= 2000000;
      }),
    videoUrl: yup.string().required('This is a required field'),
    requirements: yup.string().required('This is a required field'),
    files: yup
      .array()
      .of(yup.mixed().required('This is a required field'))
      .nullable(),
    recordings: yup
      .array()
      .of(
        yup.object({
          name: yup.string().required(),
          url: yup.string().url().required(),
        })
      )
      .nullable(),
  })
  .required();

function EditCourse({ toggleModal, course, courseId }) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      courseName: course?.courseName,
      courseDescription: course?.courseDescription,
      courseImage: course?.courseImage,
      files: course?.files,
      recordings: course?.recordings ?? [],
      videoUrl: course?.videoUrl,
      requirements: course?.requirements,
    },
  });

  const queryClient = useQueryClient();
  const requirementsEditorRef = useRef(null);
  const [requirementsIsDirty, setRequirementsIsDirty] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenMenu = (event, id) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(id);
  };

  const handleCloseMenu = () => {
    setOpenMenu(null);
  };
  const permissions = usePermissions();
  const role = useGetUserRole();

  const { mutate, isLoading: isSubmitting } = useMutation(
    (data) => axiosInstancev2.put(`/courses/${courseId}`, data),
    {
      onSuccess: () => {
        toast.success('Course updated successfully!');
        queryClient.invalidateQueries(['course', courseId]);
        queryClient.invalidateQueries(['courses']);
        toggleModal();
      },
      onError: () => {
        toast.error("Couldn't update course. Please try again.");
      },
    }
  );

  const files = watch('files') ?? [];

  const addFiles = (newFiles) => {
    Array.from(newFiles).forEach((file) => {
      fileToBase64(file).then((base64) => {
        setValue('files', [...files, { name: file.name, fileData: base64 }]);
      });
    });
  };

  const removeFile = (index) => {
    handleCloseMenu();
    const newFiles = files?.filter((_, i) => i !== index);
    setValue('files', newFiles);
  };

  // { type: 'renameFile' | 'addRecording' | 'renameRecording', index? }
  const [nameDialog, setNameDialog] = useState(null);
  const closeNameDialog = () => setNameDialog(null);

  const renameFile = (index) => {
    handleCloseMenu();
    setNameDialog({ type: 'renameFile', index });
  };

  const recordings = watch('recordings') ?? [];

  const addRecording = () => {
    setNameDialog({ type: 'addRecording' });
  };

  const removeRecording = (index) => {
    handleCloseMenu();
    setValue(
      'recordings',
      recordings.filter((_, i) => i !== index)
    );
  };

  const renameRecording = (index) => {
    handleCloseMenu();
    setNameDialog({ type: 'renameRecording', index });
  };

  const nameDialogConfig = (() => {
    if (!nameDialog) return {};
    const { type, index } = nameDialog;
    if (type === 'renameFile') {
      return {
        title: 'Rename File',
        initialName: files[index]?.name ?? '',
        submitLabel: 'Rename',
      };
    }
    if (type === 'renameRecording') {
      return {
        title: 'Edit Recording',
        initialName: recordings[index]?.name ?? '',
        initialUrl: recordings[index]?.url ?? '',
        withUrl: true,
        submitLabel: 'Save',
      };
    }
    return { title: 'Add Recording', withUrl: true, submitLabel: 'Add' };
  })();

  const submitNameDialog = ({ name, url }) => {
    const { type, index } = nameDialog;
    if (type === 'renameFile') {
      setValue(
        'files',
        files.map((file, i) => (i === index ? { ...file, name } : file))
      );
    } else if (type === 'renameRecording') {
      setValue(
        'recordings',
        recordings.map((recording, i) =>
          i === index ? { ...recording, name, url } : recording
        )
      );
    } else {
      setValue('recordings', [...recordings, { name, url }]);
    }
    closeNameDialog();
  };

  const canCreateCourseModule =
    permissions?.includes('CreateCourseModule') || role === 'SuperAdmin';

  const saveRequirementsContent = () => {
    if (requirementsEditorRef.current) {
      const content = requirementsEditorRef.current.getContent();
      setValue('requirements', content);
      setRequirementsIsDirty(false);
      requirementsEditorRef.current.setDirty(false);
    }
  };

  const downloadFile = (fileUrl, filename) => {
    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(fileUrl);
  };

  const onSubmit = async (data) => {
    let courseImage = '';
    if (data.courseImage?.[0] instanceof File) {
      courseImage = await fileToBase64(data.courseImage[0]);
    }

    mutate({
      courseName: data.courseName,
      courseDescription: data.courseDescription,
      courseImage,
      files: [...data.files],
      recordings: data.recordings ?? [],
      videoUrl: data.videoUrl,
      requirements: data.requirements,
    });
  };

  return (
    <div>
      <form
        className="text-gray-700 space-y-2"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col space-y-1">
          <label htmlFor="editCourseName" className="text-base">
            Course Name
          </label>
          <input
            {...register('courseName', { required: true })}
            type="text"
            id="editCourseName"
            placeholder="Enter Course Name"
            defaultValue={course?.courseName}
            className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
              errors.courseName ? 'border-red-600' : ''
            }`}
          />
          {errors.courseName && (
            <span className="text-red-600 text-xs mt-2">
              {errors.courseName?.message}
            </span>
          )}
        </div>
        <div className="flex flex-col space-y-1">
          <label htmlFor="editCourseDescription" className="text-base">
            Course Description
          </label>
          <textarea
            {...register('courseDescription', { required: true })}
            rows={3}
            id="editCourseDescription"
            placeholder="Enter Course Description"
            defaultValue={course?.courseDescription}
            className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
              errors.courseDescription ? 'border-red-600' : ''
            }`}
          />
          {errors.courseDescription && (
            <span className="text-red-600 text-xs mt-2">
              {errors.courseDescription?.message}
            </span>
          )}
        </div>
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col space-y-1">
            <label htmlFor="editCourseImage" className="text-base">
              Course Image
            </label>
            {course?.courseImage && (
              <div className="mb-2">
                <img
                  src={course.courseImage}
                  alt={course.courseName}
                  className="h-32 object-contain rounded-md border border-gray-200"
                />
                <p className="text-xs text-gray-400 mt-1">Current image</p>
              </div>
            )}
            <input
              {...register('courseImage')}
              accept="image/gif, image/jpeg, image/png, image/webp"
              type="file"
              id="editCourseImage"
              className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
                errors.courseImage ? 'border-red-600' : ''
              }`}
            />
            <p className="text-xs text-gray-400">
              Leave empty to keep the current image
            </p>
            {errors.courseImage && (
              <span className="text-red-600 text-xs mt-2">
                {errors.courseImage?.message}
              </span>
            )}
          </div>
          <div className="flex flex-col space-y-1 h-full relative">
            <label htmlFor="editCourseImage" className="text-base">
              Course Files
            </label>
            <div className="grid grid-cols-1 gap-4">
              {files?.map((file, index) => (
                <div
                  key={file.file_url || file.name}
                  className="flex px-4 py-2 rounded bg-gray-100 justify-between items-center"
                >
                  <p className="text-sm text-gray-500 max-w-md truncate">
                    {file.name}
                  </p>
                  <div className="flex gap-4">
                    <MoreVert
                      fontSize="small"
                      onClick={(event) =>
                        handleOpenMenu(event, `file-${index}`)
                      }
                    />
                    <Popover
                      id={anchorEl ? 'simple-popover' : undefined}
                      open={openMenu === `file-${index}`}
                      onClose={() => handleCloseMenu()}
                      anchorEl={anchorEl}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                    >
                      <List>
                        {file.file_url && (
                          <ListItem
                            onClick={() =>
                              downloadFile(file.file_url, file.name)
                            }
                            className="gap-2"
                          >
                            <DownloadForOffline className="text-gray-500 cursor-pointer" />{' '}
                            Download
                          </ListItem>
                        )}
                        <ListItem
                          onClick={() => removeFile(index)}
                          className="gap-2"
                        >
                          <Cancel className="text-red-500 cursor-pointer" />{' '}
                          Delete
                        </ListItem>
                        <ListItem
                          onClick={() => renameFile(index)}
                          className="gap-2"
                        >
                          <Edit className="text-gray-500 cursor-pointer" />{' '}
                          Rename
                        </ListItem>
                      </List>
                    </Popover>
                  </div>
                </div>
              ))}
            </div>
            <input
              onChange={(e) => {
                const newFiles = e.target.files;
                addFiles(newFiles);
              }}
              multiple
              type="file"
              id="editCourseFiles"
              className={`border-[#CBCBCB] relative bottom-0 border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
                errors.courseImage ? 'border-red-600' : ''
              }`}
            />
            <p className="text-xs text-gray-400">
              Leave empty to keep the current image
            </p>
            {errors.files && (
              <span className="text-red-600 text-xs mt-2">
                {errors.files?.message}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-base">Course Recordings</p>
            <button
              type="button"
              onClick={addRecording}
              className="border-[1px] border-claret-500 px-3 py-1 rounded-md text-claret-500 text-sm"
            >
              Add Recording
            </button>
          </div>
          {recordings.length === 0 ? (
            <p className="text-xs text-gray-400">No recordings added yet</p>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {recordings.map((recording, index) => (
                <div
                  key={`${recording.url}-${recording.name}`}
                  className="flex px-4 py-2 rounded bg-gray-100 justify-between items-center"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-gray-700 max-w-md truncate">
                      {recording.name}
                    </p>
                    <p className="text-xs text-gray-400 max-w-md truncate">
                      {recording.url}
                    </p>
                  </div>
                  <div className="flex gap-4">
                    <MoreVert
                      fontSize="small"
                      onClick={(event) =>
                        handleOpenMenu(event, `recording-${index}`)
                      }
                    />
                    <Popover
                      id={anchorEl ? 'simple-popover' : undefined}
                      open={openMenu === `recording-${index}`}
                      onClose={() => handleCloseMenu()}
                      anchorEl={anchorEl}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                    >
                      <List>
                        <ListItem
                          component="a"
                          href={recording.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleCloseMenu()}
                          className="gap-2"
                        >
                          <OpenInNew className="text-gray-500 cursor-pointer" />{' '}
                          Open
                        </ListItem>
                        <ListItem
                          onClick={() => removeRecording(index)}
                          className="gap-2"
                        >
                          <Cancel className="text-red-500 cursor-pointer" />{' '}
                          Delete
                        </ListItem>
                        <ListItem
                          onClick={() => renameRecording(index)}
                          className="gap-2"
                        >
                          <Edit className="text-gray-500 cursor-pointer" /> Edit
                        </ListItem>
                      </List>
                    </Popover>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col space-y-1">
          <label htmlFor="videoUrl" className="text-base">
            Video Url
          </label>
          <input
            {...register('videoUrl', { required: true })}
            type="text"
            id="videoUrl"
            defaultValue={course?.videoUrl}
            placeholder="Video Url"
            className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
              errors.videoUrl ? 'border-red-600' : ''
            }`}
          />
          {errors.videoUrl && (
            <span className="text-red-600 text-xs mt-2">
              {errors.videoUrl?.message}
            </span>
          )}
        </div>
        <div className="flex flex-col space-y-1 max-w-full">
          <label htmlFor="requirements" className="text-base">
            Course Requirements{' '}
            <strong className="">
              (Please enter the course prerequisites)
            </strong>
          </label>
          <Editor
            tinymceScriptSrc="/tinymce/tinymce.min.js"
            licenseKey="gpl"
            initialValue={course?.requirements}
            onInit={(evt, editor) => {
              requirementsEditorRef.current = editor;
              setValue('requirements', course?.requirements);
            }}
            onDirty={() => setRequirementsIsDirty(true)}
            init={{
              ...tinyMCEConfigs,
              min_height: 300,
              max_height: 500,
              editable_root: canCreateCourseModule,
              save_onsavecallback: saveRequirementsContent,
            }}
          />
          {requirementsIsDirty && (
            <p className="text-persian-400 text-sm">
              You have unsaved Requirements content!
            </p>
          )}
        </div>
        {isSubmitting ? (
          <div className="grid items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex justify-between mt-8">
            <button
              className="border-[1px] border-claret-500 px-4 py-1 rounded-md text-claret-500 text-sm space-x-2 flex items-center"
              type="button"
              onClick={toggleModal}
            >
              <CancelIcon fontSize="inherit" />
              <p>Cancel</p>
            </button>
            <button
              className="bg-claret-500 px-4 py-1 rounded-md text-white text-sm space-x-2 flex items-center"
              type="submit"
            >
              <SaveIcon fontSize="inherit" />
              <p>Update Course</p>
            </button>
          </div>
        )}
      </form>
      <NameUrlDialog
        open={!!nameDialog}
        onClose={closeNameDialog}
        onSubmit={submitNameDialog}
        {...nameDialogConfig}
      />
    </div>
  );
}

export default EditCourse;
